import type { CampaignRequest, CampaignRequestDetail, CampaignRequestListRow } from "@/hooks/api/use-campaign-requests";

import { LAST_STEP, WIZARD_STEPS } from "./create/steps";

/**
 * How far a request actually got, and which of the wizard's steps are done.
 *
 * There is one rule and it is worth stating plainly: a step is complete when the request holds
 * the thing that step is responsible for. Not when the client has walked past it.
 *
 * The rail used to tick a step when its index was lower than the current one, which is a claim
 * about where the cursor is rather than about the request — so opening `?step=6` on an empty
 * draft drew five ticks against five steps nobody had filled in, and the one affordance in the
 * wizard that says "this is saved" said it about nothing. The same derivation decides where a
 * draft row on the list resumes, and the two must not be able to disagree: a row that says
 * "Step 4 of 8 · Leads" and a rail that ticks Leads are describing the same request.
 *
 * Both screens read it from facts the API sends — the list row carries counts on `summary`, the
 * wizard has the whole graph — so neither has to guess and neither pays for the other's shape.
 */

/**
 * What completion is decided from, in the one shape both screens can produce.
 *
 * Counts rather than rows: the list is not given the rows and does not need them, and asking
 * this module for anything richer would put the list back to reading a detail graph per row.
 */
export interface RequestProgress {
	/** Step 1 is what the rest hangs off: what it is for, where, and in what language. */
	hasBasics: boolean;
	agentCount: number;
	/** Market rows written, and how many of those carry both an agent and a start date. */
	marketScheduleCount: number;
	scheduledMarketCount: number;
	/**
	 * A lead list attached, a volume stated, or a date promised. UX §07 makes skipping an equal
	 * path — "I'll send leads later" is an answer, so telling us when counts as answering.
	 */
	hasLeads: boolean;
	outcomeCount: number;
	/** Every compliance field is optional, so the step counts as done once any of them is set. */
	hasCompliance: boolean;
	hasQuote: boolean;
	/** The client has confirmed the quote. The only thing that finishes step 8. */
	isConfirmed: boolean;
}

/** The compliance step out of a request row. Shared by both adapters — the fields are the same. */
function complianceOf(request: CampaignRequest): boolean {
	return (
		request.recordingDisclosure ||
		request.aiDisclosure ||
		Boolean(request.transferNumber) ||
		Boolean(request.dncListDocumentId)
	);
}

function basicsOf(request: CampaignRequest): boolean {
	return Boolean(request.objective) && request.markets.length > 0 && request.languages.length > 0;
}

/** A list row, whose counts arrive on `summary` already built for the whole page. */
export function progressOfRow(row: CampaignRequestListRow): RequestProgress {
	const { summary } = row;

	return {
		hasBasics: basicsOf(row),
		agentCount: summary.agentCount,
		marketScheduleCount: summary.marketScheduleCount,
		scheduledMarketCount: summary.scheduledMarketCount,
		hasLeads: summary.hasLeadList || row.expectedContacts !== null || row.leadsDueDate !== null,
		outcomeCount: summary.outcomeCount,
		hasCompliance: complianceOf(row),
		hasQuote: summary.hasQuote,
		isConfirmed: row.confirmedAt !== null,
	};
}

/** The wizard's own copy, which holds the whole graph and counts it directly. */
export function progressOfRequest(request: CampaignRequestDetail): RequestProgress {
	return {
		hasBasics: basicsOf(request),
		agentCount: request.agents.length,
		marketScheduleCount: request.marketSchedules.length,
		scheduledMarketCount: request.marketSchedules.filter((market) => market.agentId && market.dialStartDate).length,
		hasLeads:
			request.documents.some((document) => document.kind === "lead_list") ||
			request.expectedContacts !== null ||
			request.leadsDueDate !== null,
		outcomeCount: request.outcomes.length,
		hasCompliance: complianceOf(request),
		hasQuote: Boolean(request.currentQuote),
		isConfirmed: request.confirmedAt !== null,
	};
}

/**
 * Which steps are done, by index, matching {@link WIZARD_STEPS}.
 *
 * Step 0 is "Start" and it is complete as soon as the request exists — reaching any of this at
 * all means a draft was written, which is exactly what that step is for.
 */
export function completedSteps(progress: RequestProgress): boolean[] {
	return [
		true,
		progress.hasBasics,
		progress.agentCount > 0,
		// Every market row, not merely one: a schedule that covers three markets out of four is
		// not a finished step, and ticking it is how the fourth goes unnoticed until provisioning.
		progress.marketScheduleCount > 0 && progress.scheduledMarketCount === progress.marketScheduleCount,
		progress.hasLeads,
		progress.outcomeCount > 0,
		progress.hasCompliance,
		progress.hasQuote,
		progress.isConfirmed,
	];
}

/**
 * The first step with work still owed on it — where a draft row reopens.
 *
 * Read in order, so a request with agents but no schedule resumes at the schedule rather than at
 * the far end of the wizard. Everything done resumes at the last step, which is the one that is
 * still waiting for a signature.
 */
export function resumeStep(progress: RequestProgress): number {
	const completed = completedSteps(progress);

	for (let step = 1; step < LAST_STEP; step++) {
		if (!completed[step]) return step;
	}

	return LAST_STEP;
}

/** What the rail calls a step, for the list's "Step 4 of 8 · Leads" line. */
export function stepLabel(step: number): string {
	return WIZARD_STEPS[step]?.label ?? "";
}

/** Re-exported so a screen reading a resume step gets the count it is out of from one import. */
export { LAST_STEP } from "./create/steps";
